import i18n from "@/i18n.ts";
import { ApiEmployee, ApiTableRowPending } from "@/types/employees/employee.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { ApiCompensationChangeReason } from "@/types/compensation-change-reason.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import {
  ApiCompensationComponentKind,
  ApiCompensationComponentShort,
} from "@/types/compensation-component.types.ts";
import { ApiCustomField, ApiCustomFieldValue } from "@/types/custom-field.types.ts";

export interface ApiEmployeeCompensationSave {
  date_effective_from: Date;
  change_reason_id: number | null;
  base_salary_gross: number | null;
  base_salary_net: number | null;
  currency: string | null;
  comment: string | null;
  per_type: ApiEmployeeCompensationPerTypes;
}

export enum ApiEmployeeCompensationPerTypes {
  hourly = "hourly",
  weekly = "weekly",
  monthly = "monthly",
  quarterly = "quarterly",
  yearly = "yearly",
}

export enum CompensationComponentRateTypes {
  fixed_amount = "fixed_amount",
  percent_of_statutory_base = "percent_of_statutory_base",
}

export interface CompensationLine {
  id: number;
  compensation_component_id: number;
  component: ApiCompensationComponentShort;
  amount_gross: string | null;
  amount_net: string | null;
  currency_code: string;
  per_type: ApiEmployeeCompensationPerTypes;
  sort: number;
  description: string | null;
  rate_type: CompensationComponentRateTypes;
  percent: string | null;
  kind: ApiCompensationComponentKind;
  is_included_in_gross: boolean;
  is_payable_to_employee: boolean;
  is_included_in_total_cost: boolean;
  is_off_payroll: boolean;
  is_statutory_base: boolean;
}

export interface CompensationRollups {
  currency_code: string;
  per_type: ApiEmployeeCompensationPerTypes;
  date_fx_rate: Date | null;
  gross_amount: string | null;
  net_official_amount: string | null;
  net_actual_amount: string | null;
  total_cost_official_amount: string | null;
  total_cost_actual_amount: string | null;
  implied_deductions: string | null;
}

export interface CompensationLineSave {
  id?: number;
  compensation_component_id: number;
  amount_gross: number | null;
  amount_net: number | null;
  currency_code: string | null;
  per_type: ApiEmployeeCompensationPerTypes | null;
  sort: number | null;
  description: string | null;
  rate_type?: CompensationComponentRateTypes | null;
  percent?: number | null;
}

export interface CompensationPackageSave {
  employee_id: number;
  date_effective_from: Date;
  custom_fields?: Record<string, ApiCustomFieldValue | null>;
  currency: string;
  change_reason_id: number | null;
  comment: string | null;
  lines: CompensationLineSave[];
}

export interface CompensationPreview {
  rollups: CompensationRollups;
  is_rollup_incomplete: boolean;
  is_rollup_estimated: boolean;
  missing_fx_line_ids: number[];
  missing_amount_line_ids: number[];
}

export interface ApiEmployeeCompensationSummary {
  package_id: number;
  date_effective_from: Date | null;
  datetime_updated_at: Date;
  rollups: CompensationRollups;
  is_rollup_incomplete: boolean;
  is_rollup_estimated: boolean;
  missing_fx_line_ids: number[];
  missing_amount_line_ids: number[];
  lines: CompensationLine[] | null;
}

export interface ApiEmployeeCompensation {
  id: number;
  date_effective_from: Date;
  date_effective_to?: Date;
  change_reason: ApiCompensationChangeReason | null;
  base_salary_gross: string | null;
  base_salary_net: string | null;
  currency: string | null;
  comment: string | null;
  per_type: ApiEmployeeCompensationPerTypes;
  changed_by?: ApiEmployee | null;
  lines?: CompensationLine[] | null;
  rollups?: CompensationRollups | null;
  is_rollup_incomplete?: boolean | null;
  is_rollup_estimated?: boolean | null;
  missing_fx_line_ids?: number[] | null;
  missing_amount_line_ids?: number[] | null;
  custom_fields?: ApiCustomField[];
}

export interface ApiEmployeeCompensationPendingRow extends Omit<
  ApiEmployeeCompensation,
  "id" | "date_effective_from" | "date_effective_to"
> {
  id: number | null;
  date_effective_from: Date | null;
  date_effective_to: Date | null;
  pending: ApiTableRowPending;
}

export interface ApiEmployeeCompensationPagedData extends ApiPagedData<ApiEmployeeCompensation> {
  pending_rows: ApiEmployeeCompensationPendingRow[];
}

export const getEmployeeCompensationPerTypeTitle = (
  per_type: ApiEmployeeCompensationPerTypes
): string => {
  if (per_type === ApiEmployeeCompensationPerTypes.hourly) {
    return i18n.t("common:per_hour");
  } else if (per_type === ApiEmployeeCompensationPerTypes.weekly) {
    return i18n.t("common:per_week");
  } else if (per_type === ApiEmployeeCompensationPerTypes.monthly) {
    return i18n.t("common:per_month");
  } else if (per_type === ApiEmployeeCompensationPerTypes.quarterly) {
    return i18n.t("common:per_quarter");
  } else {
    return i18n.t("common:per_year");
  }
};

export const getEmployeeCompensationPerTypes = () => {
  return [
    {
      id: ApiEmployeeCompensationPerTypes.hourly,
      title: getEmployeeCompensationPerTypeTitle(ApiEmployeeCompensationPerTypes.hourly),
    },
    {
      id: ApiEmployeeCompensationPerTypes.weekly,
      title: getEmployeeCompensationPerTypeTitle(ApiEmployeeCompensationPerTypes.weekly),
    },
    {
      id: ApiEmployeeCompensationPerTypes.monthly,
      title: getEmployeeCompensationPerTypeTitle(ApiEmployeeCompensationPerTypes.monthly),
    },
    {
      id: ApiEmployeeCompensationPerTypes.quarterly,
      title: getEmployeeCompensationPerTypeTitle(ApiEmployeeCompensationPerTypes.quarterly),
    },
    {
      id: ApiEmployeeCompensationPerTypes.yearly,
      title: getEmployeeCompensationPerTypeTitle(ApiEmployeeCompensationPerTypes.yearly),
    },
  ] as SelectItem[];
};
